import { useState } from "react";
import {
  Box as MuiBox,
  CircularProgress as MuiCircularProgress,
} from "@mui/material";
import { DataGridPro as MuiDataGrid } from "@mui/x-data-grid-pro";
import {
  Check as MuiCheckIcon,
  Loop as MuiLoopIcon,
  Warning as MuiWarningIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  getTableColumnHeaders,
  tableStyles,
} from "./OISRecentActivityTableConfig";
import { LoadingGridOverlay, NoRowsGridOverlay } from "common";
import { LOADER_TYPES } from "app/services/OISService/constants";
import { useOIS } from "app/services/OISService/OISProvider";
import RecentActivityDialog from "./RecentActivityDialog";

const OISRecentActivityTable = () => {
  const { t } = useTranslation();
  const [openRecentActivityDialog, setOpenRecentActivityDialog] =
    useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const { isLoading, recentActivityRecords, searchFilters, downloadFile } =
    useOIS();

  const recentActivityRecordsFiltered = {
    participants: t("ois.table.filter.participantRegistration"),
    review: t("ois.table.filter.continuingReview"),
    training: t("ois.table.filter.trainingData"),
  };

  const filteredRecords = searchFilters.action
    ? recentActivityRecords.filter(
        (record) =>
          record.type ===
          recentActivityRecordsFiltered[searchFilters.action.value],
      )
    : recentActivityRecords;

  const getStatusIcon = (recentEntryStatus) => {
    if (recentEntryStatus === t("ois.table.statusIcons.finished")) {
      return <MuiCheckIcon color="primary" sx={{ fontSize: "20px" }} />;
    } else if (recentEntryStatus === t("ois.table.statusIcons.uploaded")) {
      return <MuiLoopIcon color="primary" sx={{ fontSize: "20px" }} />;
    } else if (
      recentEntryStatus === t("ois.table.statusIcons.parsed") ||
      recentEntryStatus === t("ois.table.statusIcons.processing")
    ) {
      return <MuiCircularProgress color="primary" sx={{ fontSize: "20px" }} />;
    } else if (
      recentEntryStatus === t("ois.table.statusIcons.failed") ||
      recentEntryStatus === t("ois.table.statusIcons.incomplete")
    ) {
      return <MuiWarningIcon color="error" sx={{ fontSize: "20px" }} />;
    }
  };

  const getRecords = (row) => {
    if (row.numRowsProcessed === row.numRows) {
      return `${row.numRows}`;
    } else {
      return `${row.numRowsProcessed}/${row.numRows}`;
    }
  };

  const handleOpenRecentActivityDialog = (row) => {
    setOpenRecentActivityDialog(true);
    setSelectedRow(row);
  };

  const handleCloseRecentActivityDialog = () => {
    setOpenRecentActivityDialog(false);
    setSelectedRow(null);
  };

  const columnConfig = getTableColumnHeaders({
    t,
    getStatusIcon,
    getRecords,
    handleOpenRecentActivityDialog,
    downloadFile,
  });

  const NoOisRecentActivityOverlay = () => (
    <NoRowsGridOverlay message={t("ois.table.noRows")} />
  );

  return (
    <MuiBox id="ois-recent-activity-table-box" sx={{ width: "100%" }}>
      <MuiDataGrid
        slots={{
          loadingOverlay: LoadingGridOverlay,
          noRowsOverlay: NoOisRecentActivityOverlay,
        }}
        autoHeight
        disableColumnMenu
        disableColumnResize
        disableColumnReorder
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        disableMultipleColumnsSorting
        disableRowSelectionOnClick
        pagination
        onPaginationModelChange={() => {
          window.scroll({
            top: 0,
            left: 0,
            behavior: "smooth",
          });
        }}
        loading={isLoading[LOADER_TYPES.OIS_DATA]}
        rows={filteredRecords}
        getRowId={(row) => `${row.filename}-${row.uploadedDate}`}
        columns={columnConfig}
        initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
        pageSizeOptions={[20, 50, 100, 500]}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd"
        }
        getRowHeight={() => "auto"}
        sx={tableStyles}
      />

      {openRecentActivityDialog && (
        <RecentActivityDialog
          isOpen={openRecentActivityDialog}
          onClose={handleCloseRecentActivityDialog}
          filename={selectedRow.filename}
          rowType={t(`importTypes.${selectedRow.type}`)}
          recentActivitySummary={[selectedRow.summary]}
        />
      )}
    </MuiBox>
  );
};

export default OISRecentActivityTable;
